import type { VatReason } from '@/types'

type Translate = (key: string, options?: Record<string, unknown>) => string

/** The VAT snapshot as it is stored on rent terms or on a rental. */
export interface VatSnapshotFields {
	vatPercent?: string
	vatIncluded?: boolean
	vatReason?: VatReason
}

/**
 * One line describing a saved VAT snapshot, for read-only views.
 *
 * A bare percent withholds the very thing the terms turn on: at 7% on 50 000,
 * "included" settles the debt at 50 000 and "on top" at 53 500. A zero rate has
 * no split to state — there the question is which of the three legal grounds
 * applies, so the reason is printed in its place.
 *
 * Terms saved before the rate was derived carry no reason. Their `vatIncluded`
 * is the column default rather than a recorded answer, so the qualifier is
 * withheld instead of asserting a term of the contract nobody chose.
 *
 * The wording is taken from the shared `properties.rentTerms.vat` vocabulary
 * that `VatSummary` already uses, so the editing form and the card it saves
 * into cannot drift apart. Note this is interface copy, not the legal clause:
 * the contract wording is `vatClause`, composed server-side and never i18n'd.
 */
export function formatVatTerm(
	{ vatPercent, vatIncluded, vatReason }: VatSnapshotFields,
	t: Translate
): string | undefined {
	if (vatPercent == null) return undefined

	const vat = 'properties.rentTerms.vat'

	if (Number(vatPercent) === 0) {
		return vatReason ? t(`${vat}.reasons.${vatReason}`) : `${vatPercent}%`
	}

	if (!vatReason) return `${vatPercent}%`

	const split = vatIncluded ? t(`${vat}.inPrice`) : t(`${vat}.onTop`)
	return `${vatPercent}% (${split})`
}
